import {
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  Divider,
  CircularProgress,
  IconButton,
  Tooltip,
  useTheme,
  Badge,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import ClearIcon from "@mui/icons-material/Clear";
import RefreshIcon from "@mui/icons-material/Refresh";
import React, { useMemo, useEffect } from "react";
import { useOptions } from "../../contexts/OptionsContext";
import { ApiEndpoint } from "../../types";

interface FilterToolbarProps {
  config: {
    apiEndpoints: ApiEndpoint[];
  };
  activeFilters: Record<string, string>;
  onFilterChange: (key: string, value: string, clearAll?: boolean) => void;
  onBulkEdit?: () => void;
  onHide: () => void;
}

const FilterToolbar: React.FC<FilterToolbarProps> = ({
  config,
  activeFilters,
  onFilterChange,
  onBulkEdit,
  onHide,
}) => {
  const { options, loadOptionsForEndpoint, isLoading, refreshOptions } = useOptions();
  const theme = useTheme();

  useEffect(() => {
    const tablesToLoad = config.apiEndpoints
      .filter(endpoint => endpoint.tableName !== "media")
      .map(endpoint => endpoint.tableName);
    
    if (tablesToLoad.length > 0) {
      console.log("Loading options for filters:", tablesToLoad);
      if (!isLoading) {
        loadOptionsForEndpoint(tablesToLoad).catch(error => {
          console.error("Failed to load options for filters:", error);
        });
      }
    }
  }, [config.apiEndpoints, loadOptionsForEndpoint]);

  const relationshipFilters = useMemo(() => {
    const filters = config.apiEndpoints
      .filter((endpoint) => endpoint.tableName !== "media")
      .map((endpoint) => {
        const endpointOptions = options?.[endpoint.tableName] || [];
        console.log(`Options for ${endpoint.name} (${endpoint.tableName}):`, endpointOptions.length);
        
        return {
          key: endpoint.queryParam,
          name: endpoint.name,
          options: endpointOptions,
          tableName: endpoint.tableName,
        };
      });
    
    return filters;
  }, [config.apiEndpoints, options]);

  const activeFilterCount = Object.values(activeFilters).filter(
    (value) => value !== ""
  ).length;

  const handleFilterChange = (key: string, value: any) => {
    onFilterChange(key, value, false);
  };

  const handleClearFilters = () => {
    onFilterChange("", "", true);
  };

  const handleRefreshOptions = () => {
    console.log("Manually refreshing filter options...");
    refreshOptions().catch(error => {
      console.error("Failed to refresh options manually:", error);
    });
  };

  return (
    <Box
      sx={{
        p: 1,
        borderBottom: "1px solid",
        borderColor: theme.palette.grey[700],
        bgcolor: theme.palette.grey[900],
        color: theme.palette.common.white,
        pointerEvents: "auto", // Ensure this component can receive clicks
        position: "relative",  // Establish stacking context
        zIndex: 1,            // Ensure it's above other elements
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title="Hide Filters">
            <IconButton 
              onClick={onHide}
              color="primary"
              size="small"
              sx={{ color: 'primary.light' }}
            >
              <Badge badgeContent={activeFilterCount} color="secondary">
                <FilterListIcon />
              </Badge>
            </IconButton>
          </Tooltip>
          <Typography 
            variant="caption" 
            sx={{ color: 'grey.400', cursor: 'pointer' }}
            onClick={onHide}
          >
            Hide Filters
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title="Refresh Filter Options">
            <span>
              <IconButton
                size="small"
                onClick={handleRefreshOptions}
                disabled={isLoading}
                color="primary"
                sx={{ color: 'primary.light' }}
              >
                {isLoading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
              </IconButton>
            </span>
          </Tooltip>

          <Button
            variant="outlined"
            size="small"
            startIcon={<ClearIcon sx={{ color: activeFilterCount > 0 && !isLoading ? 'error.light' : 'grey.600' }}/>}
            onClick={handleClearFilters}
            disabled={activeFilterCount === 0 || isLoading}
            sx={{ 
              color: activeFilterCount > 0 && !isLoading ? 'error.light' : 'grey.600',
              borderColor: activeFilterCount > 0 && !isLoading ? 'error.light' : 'grey.600',
              '&:hover': { 
                borderColor: activeFilterCount > 0 && !isLoading ? 'error.main' : 'grey.500',
                bgcolor: 'rgba(255, 255, 255, 0.08)'
              },
            }}
          >
            Clear Filters
          </Button>
          
          {onBulkEdit && (
            <Button
              variant="contained"
              size="small"
              onClick={onBulkEdit}
              color="primary"
              disabled={activeFilterCount === 0 || isLoading}
            >
              Bulk Edit
            </Button>
          )}
        </Box>
      </Box>

      <Box>
        {activeFilterCount > 0 && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1, mt: 1 }}>
            {Object.entries(activeFilters)
              .filter(([_, value]) => value !== "")
              .map(([key, value]) => {
                const filter = relationshipFilters.find((f) => f.key === key);
                if (!filter) return null;

                const option = filter.options.find(
                  (o) => String(o.id) === String(value)
                );
                if (!option) return null;

                return (
                  <Chip
                    key={key}
                    label={`${filter.name}: ${option.name}`}
                    onDelete={() => handleFilterChange(key, "")}
                    size="small"
                    sx={{ 
                      bgcolor: 'secondary.dark',
                      color: 'common.white',
                      '.MuiChip-deleteIcon': { 
                        color: 'rgba(255, 255, 255, 0.7)',
                        '&:hover': { color: 'rgba(255, 255, 255, 1)' }
                      }
                    }}
                    variant="filled"
                  />
                );
              })}
          </Box>
        )}

        <Divider sx={{ my: 1, borderColor: theme.palette.grey[700] }} />

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
          {isLoading && relationshipFilters.length === 0 ? (
            <Box sx={{ display: 'flex', alignItems: 'center', p: 1, width: '100%' }}>
              <CircularProgress size={20} sx={{ mr: 1, color: 'primary.light' }} />
              <Typography variant="body2" sx={{ color: 'grey.300' }}>Loading filters...</Typography>
            </Box>
          ) : relationshipFilters.length === 0 && !isLoading ? (
            <Typography variant="body2" color="text.secondary" sx={{ color: 'grey.500' }}>No filter options available</Typography>
          ) : (
            relationshipFilters.map((filter) => (
              <FormControl
                key={filter.key}
                size="small"
                sx={{ minWidth: 150, maxWidth: 200 }}
                disabled={isLoading}
                variant="outlined"
              >
                <InputLabel 
                  id={`filter-label-${filter.key}`}
                  sx={{ 
                    color: 'grey.300',
                    '&.Mui-focused': { color: 'primary.light' }
                  }} 
                >
                  {filter.name}
                </InputLabel>
                <Select
                  labelId={`filter-label-${filter.key}`}
                  value={activeFilters[filter.key] || ""}
                  onChange={(e) => handleFilterChange(filter.key, e.target.value)}
                  label={filter.name}
                  sx={{ 
                    color: 'common.white',
                    '.MuiOutlinedInput-notchedOutline': { 
                      borderColor: 'transparent'
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': { 
                      borderColor: 'grey.400'
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { 
                      borderColor: 'primary.light'
                    },
                    '.MuiSvgIcon-root': { color: 'grey.400' }
                  }}
                  MenuProps={{
                    PaperProps: {
                      sx: { 
                        maxHeight: 300,
                        bgcolor: theme.palette.grey[800],
                        color: 'common.white'
                      }
                    }
                  }}
                >
                  <MenuItem value="" sx={{ color: 'grey.300' }}>
                    <em>Any</em>
                  </MenuItem>
                  {filter.options.length === 0 ? (
                    <MenuItem disabled sx={{ color: 'grey.500' }}>
                      <em>{isLoading ? 'Loading...' : 'No options'}</em>
                    </MenuItem>
                  ) : (
                    filter.options.map((option) => (
                      <MenuItem 
                        key={option.id} 
                        value={option.id} 
                        sx={{ 
                          color: 'common.white',
                          '&.Mui-selected': {
                            bgcolor: 'rgba(255, 255, 255, 0.1)',
                            '&:hover': {
                              bgcolor: 'rgba(255, 255, 255, 0.15)'
                            }
                          },
                          '&:hover': {
                            bgcolor: 'rgba(255, 255, 255, 0.08)'
                          }
                        }}
                      >
                        {option.name}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            ))
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default FilterToolbar; 